import { useState, type MouseEvent as ReactMouseEvent } from "react";
import { Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bookdental/ui";
import { cn } from "@/lib/cn";

export type ColorSelectOption = { value: string; label: string; bg: string; color: string };

/** Select colorido — o fechado e cada item da lista aberta usam a cor do próprio item, em vez do
 *  cinza/branco padrão do navegador. Usa o `Select` do shadcn (Radix) por baixo: `<option>` nativo
 *  não dá controle de estilo confiável na lista aberta entre navegadores (fundo "estranho" ao
 *  abrir), o Radix renderiza a lista como HTML de verdade, então a cor aplica igual ao fechado. */
export function ColorSelect({
  value,
  options,
  disabled,
  size = "sm",
  className,
  onChange,
  onClick,
  acaoRodape,
}: {
  value: string;
  options: ColorSelectOption[];
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  onChange: (value: string) => void;
  onClick?: (e: ReactMouseEvent) => void;
  /** Item de ação no fim da lista (ex: "Novo estágio") — fora da lista de opções de propósito:
   *  não é um valor selecionável, é um atalho pra criar um. */
  acaoRodape?: { label: string; onClick: () => void };
}) {
  const current = options.find((o) => o.value === value);
  // Aberto controlado só por causa da ação de rodapé: sem fechar a lista antes de abrir o
  // diálogo, o Radix continua montado por trás dele e engole a digitação (typeahead).
  const [aberto, setAberto] = useState(false);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled} open={aberto} onOpenChange={setAberto}>
      <SelectTrigger
        onClick={onClick}
        className={cn(
          "gap-1 truncate rounded-full border-0 font-bold uppercase shadow-none focus:ring-1 focus:ring-brand-500",
          size === "sm" ? "h-5 px-1.5 py-0 text-[9px] [&>svg]:h-3 [&>svg]:w-3" : "h-7 px-2 text-[11px] [&>svg]:h-3.5 [&>svg]:w-3.5",
          className,
        )}
        style={{ backgroundColor: current?.bg, color: current?.color }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        onClick={(e) => e.stopPropagation()}
        className="border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card"
      >
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            className="text-xs font-semibold"
            style={{ backgroundColor: o.bg, color: o.color }}
          >
            {o.label}
          </SelectItem>
        ))}
        {acaoRodape && (
          <button
            type="button"
            // Só `stopPropagation` no pointerdown — sem `preventDefault`, que suprimiria o clique
            // deste botão e deixaria o clique sintetizado cair na linha da tabela por baixo,
            // abrindo o detalhe do serviço junto.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setAberto(false);
              acaoRodape.onClick();
            }}
            className="mt-1 flex w-full items-center gap-1.5 border-t border-zinc-100 px-2 py-2 text-xs font-semibold text-brand-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-primary dark:hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" />
            {acaoRodape.label}
          </button>
        )}
      </SelectContent>
    </Select>
  );
}
